import ClearIcon from "@mui/icons-material/Clear";
import ConnectingAirportsIcon from "@mui/icons-material/ConnectingAirports";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Tenant } from "../../types";
import { tailNumberIncludes } from "../../utils/tailNumberUtils";

type Props = {
  mini?: boolean;
  tenants: Tenant[];
  onSelect: (tenant: Tenant) => void;
};

export const AircraftFinder: React.FC<Props> = ({ mini, ...props }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "searchPopover" : undefined;

  if (mini) {
    return (
      <>
        <Tooltip title={t('screens.ramp.aircraftFinder.tooltip')}>
          <Button
            size="small"
            variant="contained"
            color="info"
            onClick={handleClick}
            sx={{
              alignSelf: "center",
              width: "80%",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <ConnectingAirportsIcon />
              <SearchIcon />
            </Stack>
          </Button>
        </Tooltip>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box width={300}>
            <AircraftFinderAutocomplete {...props} />
          </Box>
        </Popover>
      </>
    );
  }

  return <AircraftFinderAutocomplete {...props} />;
};

const AircraftFinderAutocomplete: React.FC<Props> = ({ tenants, onSelect }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = React.useState("");

  const options = React.useMemo(
    () =>
      tenants.sort((a, b) => {
        const cmpTail = a.tail_number.localeCompare(b.tail_number);
        if (cmpTail !== 0) {
          return cmpTail;
        }
        return a.aircraft.model.localeCompare(b.aircraft.model);
      }),
    [tenants.length]
  );

  // Custom filter function to handle hyphen-flexible tail number matching
  const filterOptions = React.useCallback(
    (options: Tenant[], state: { inputValue: string }) => {
      const query = state.inputValue;
      if (!query) return options;
      return options.filter(
        (option) =>
          tailNumberIncludes(option.tail_number, query) ||
          option.aircraft.model.toLowerCase().includes(query.toLowerCase())
      );
    },
    []
  );

  return (
    <Box sx={{ px: 1.5, pb: 1 }}>
      <Autocomplete
        fullWidth
        size="small"
        popupIcon={null}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(event: any, newValue: Tenant | null) => {
          if (!newValue || !newValue?.id) {
            return;
          }
          onSelect(newValue);
        }}
        options={options}
        filterOptions={filterOptions}
        getOptionLabel={(option: Tenant) => {
          return option.tail_number ?? "";
        }}
        renderOption={(props, option) => {
          const isInHangar = tenants.find((tenant) => tenant.id === option.id);
          let text = "";
          if (!option.tail_number) {
            text = `${option.aircraft.model}`;
          } else {
            text = `${option.tail_number} - ${option.aircraft.model}`;
          }

          return (
            <li
              {...props}
              key={`search-hangars-${option.id}`}
              style={{
                backgroundColor: isInHangar ? `#c8cfde40` : "transparent",
              }}
            >
              <Typography variant="inherit">{text}</Typography>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={t('screens.ramp.aircraftFinder.searchPlaceholder')}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: inputValue && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setInputValue("")}
                    edge="end"
                    aria-label={t('screens.ramp.aircraftFinder.clear')}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};
